import type { Profile, BandRole } from '../core/types';
import type { TrainingClass } from '../render/sam';

export type Mode = 'index' | 'rgb' | 'cir' | 'custom' | 'single';
export type MaskMode = 'auto' | 'value' | 'alpha' | 'off';

export interface State {
  profile: Profile | null;
  status: { message: string; error: boolean; fix: string };
  busy: boolean;
  mode: Mode;
  indexId: string;
  rampId: string;
  lo: number; hi: number; gamma: number;
  singleBand: number;
  customBands: [number, number, number];
  clipRange: boolean;
  balanceBands: boolean;
  lockRamp: boolean;
  rampLo: number | null; rampHi: number | null;
  classify: boolean; classN: number; classMethod: 'equal' | 'quantile';
  maskMode: MaskMode;
  theme: 'dark' | 'light';
  scaleUnits: 'metric' | 'us' | 'nautical' | 'degrees';
  basemap: 'esri' | 'carto' | 'osm' | 'none';
  customSrc: string;
  overTrue: boolean;
  /** What to draw beneath a classification or index: 'true', 'cir', or an index id. */
  underlay: string;
  opacity: number;
  /** Ids of collapsed sidebar sections. */
  collapsed: string[];
  /** Training classes for the spectral-angle classifier. */
  classes: TrainingClass[];
  /** Which class a map click adds a training point to. */
  activeClass: number;
  /** Beyond this angle a pixel matches nothing and stays unclassified. */
  samMaxAngle: number;
  /** Drag on the map to collect training pixels instead of panning. */
  brush: boolean;
  /** Brush radius, in sampled grid cells. */
  brushRadius: number;
}

export const initialState: State = {
  profile: null,
  status: { message: 'Ready. Bands are identified from headers and the smallest overview before anything renders.', error: false, fix: '' },
  busy: false,
  mode: 'index', indexId: 'ndvi', rampId: 'rdylgn',
  lo: -0.1, hi: 0.9, gamma: 2.2,
  singleBand: 0, customBands: [0, 1, 2],
  clipRange: false, balanceBands: true, lockRamp: false, rampLo: null, rampHi: null,
  classify: false, classN: 5, classMethod: 'equal',
  maskMode: 'auto', theme: 'dark', scaleUnits: 'metric', basemap: 'esri',
  customSrc: '(nir - red) / (nir + red)',
  overTrue: false,
  underlay: 'true',
  opacity: 1,
  collapsed: ['source'],
  classes: [],
  activeClass: 0,
  samMaxAngle: 10,
  brush: false,
  brushRadius: 3,
};

type Listener = (s: State) => void;

/**
 * One state object, one subscribe. Panels derive from state rather than being
 * imperatively re-rendered.
 *
 * The bug this exists to kill: four separate times, state updated correctly and the
 * matching render call was simply forgotten — a dropdown that stayed disabled, a panel
 * that never appeared. There is no render call to forget here.
 */
export function createStore(init: State = initialState) {
  let state = { ...init };
  const listeners = new Set<Listener>();
  return {
    get: (): State => state,
    set(patch: Partial<State>) {
      state = { ...state, ...patch };
      listeners.forEach((fn) => fn(state));
    },
    subscribe(fn: Listener): () => void {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    /** Roles are edited in place on the profile, so signal a change explicitly. */
    setBandRole(index: number, role: BandRole, wavelengthNm: number | null) {
      if (!state.profile) return;
      const bands = state.profile.bands.map((b, i) =>
        i === index ? { ...b, role, wavelengthNm, source: 'manual' as const, why: 'set by hand' } : b);
      this.set({ profile: { ...state.profile, bands } });
    },
  };
}
export type Store = ReturnType<typeof createStore>;
